import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Phone, MapPin, Calendar, Save, Edit, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import AddressInput from '@/components/ui/AddressInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileManagement = ({ user, member, onUpdateSuccess }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: member?.phone || '',
    address: member?.address || '',
    emergency_contact: member?.emergency_contact || '',
    emergency_phone: member?.emergency_phone || ''
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: '', color: 'gray' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { level: 1, text: t('profile.weak'), color: 'red' };
    if (strength <= 4) return { level: 2, text: t('profile.medium'), color: 'yellow' };
    return { level: 3, text: t('profile.strong'), color: 'green' };
  };

  const passwordStrength = getPasswordStrength(passwordData.new_password);
  const passwordsMatch = passwordData.new_password === passwordData.confirm_password && passwordData.confirm_password !== '';

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (passwordData.new_password.length < 8) {
      toast.error(t('profile.passwordMinLength'));
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t('profile.passwordsDoNotMatch'));
      return;
    }

    setPasswordLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('profile.passwordChanged'));
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || t('profile.failedToChangePassword'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update user info (name and email)
      const userResponse = await axios.put(`${API}/auth/profile`, {
        full_name: formData.full_name,
        email: formData.email
      });

      // Update member info (phone, address, emergency contacts)
      if (member) {
        await axios.put(`${API}/members/${member.id}`, {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          emergency_contact: formData.emergency_contact,
          emergency_phone: formData.emergency_phone
        });
      }

      // Update local storage with new user data to reflect changes immediately
      if (userResponse.data?.user) {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = { ...currentUser, ...userResponse.data.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      toast.success(t('profile.profileUpdated'));
      setEditing(false);
      if (onUpdateSuccess) onUpdateSuccess();
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || t('profile.failedToUpdate'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Personal Information Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('profile.personalInformation')}</CardTitle>
              <CardDescription>{t('profile.manageProfileDetails')}</CardDescription>
            </div>
            {!editing && (
              <Button
                variant="outline"
                onClick={() => setEditing(true)}
              >
                <Edit className="h-4 w-4 mr-2" />
                {t('profile.editProfile')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="full_name" className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-600" />
                  {t('profile.fullName')}
                </Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-600" />
                  {t('profile.emailAddress')}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-600" />
                  {t('profile.phoneNumber')}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                />
              </div>

              {/* Address */}
              <AddressInput
                value={formData.address}
                onChange={handleChange}
                onAddressSelected={(data) => {
                  setFormData(prev => ({
                    ...prev,
                    address: data.formatted_address
                  }));
                }}
                disabled={!editing}
                className={!editing ? 'bg-gray-50' : ''}
                label={t('profile.address')}
              />

              {/* Emergency Contact */}
              <div className="space-y-2">
                <Label htmlFor="emergency_contact" className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-600" />
                  {t('profile.emergencyContactName')}
                </Label>
                <Input
                  id="emergency_contact"
                  name="emergency_contact"
                  value={formData.emergency_contact}
                  onChange={handleChange}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                />
              </div>

              {/* Emergency Phone */}
              <div className="space-y-2">
                <Label htmlFor="emergency_phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-600" />
                  {t('profile.emergencyContactPhone')}
                </Label>
                <Input
                  id="emergency_phone"
                  name="emergency_phone"
                  value={formData.emergency_phone}
                  onChange={handleChange}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                />
              </div>
            </div>

            {editing && (
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? t('common.saving') : t('common.saveChanges')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      full_name: user?.full_name || '',
                      email: user?.email || '',
                      phone: member?.phone || '',
                      address: member?.address || '',
                      emergency_contact: member?.emergency_contact || '',
                      emergency_phone: member?.emergency_phone || ''
                    });
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-teal-600" />
            <div>
              <CardTitle>{t('profile.changePassword')}</CardTitle>
              <CardDescription>{t('profile.updateAccountPassword')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="current_password">{t('profile.currentPassword')}</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  name="current_password"
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  placeholder={t('profile.enterCurrentPassword')}
                  className="pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="new_password">{t('profile.newPassword')}</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  name="new_password"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  placeholder={t('profile.enterNewPassword')}
                  className="pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {/* Password Strength Indicator */}
              {passwordData.new_password && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3].map((level) => (
                      <div
                        key={level}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                          passwordStrength.level >= level
                            ? passwordStrength.color === 'red'
                              ? 'bg-red-500'
                              : passwordStrength.color === 'yellow'
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs ${
                    passwordStrength.color === 'red' ? 'text-red-600' :
                    passwordStrength.color === 'yellow' ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {t('profile.passwordStrength')}: {passwordStrength.text}
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-500">
                {t('profile.passwordRequirements')}
              </p>
            </div>

            {/* Confirm New Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm_password">{t('profile.confirmNewPassword')}</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  placeholder={t('profile.confirmYourNewPassword')}
                  className={`pr-10 ${
                    passwordData.confirm_password && !passwordsMatch
                      ? 'border-red-500 focus:ring-red-500'
                      : passwordsMatch
                      ? 'border-green-500 focus:ring-green-500'
                      : ''
                  }`}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordData.confirm_password && (
                <div className="flex items-center gap-1">
                  {passwordsMatch ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-xs text-green-600">{t('profile.passwordsMatch')}</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span className="text-xs text-red-600">{t('profile.passwordsDoNotMatch')}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={passwordLoading || !passwordData.current_password || !passwordData.new_password || !passwordsMatch}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Lock className="h-4 w-4 mr-2" />
              {passwordLoading ? t('common.updating') : t('profile.updatePassword')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileManagement;
